/**
 * ErrorBudgetChart.tsx — SVG sparkline for SLO error budget over time
 *
 * Purpose:    Renders an inline SVG line chart showing error budget percentage
 *             over a time window, with color-coded thresholds and grid lines.
 * Inputs:     points: ErrorBudgetPoint[] — time-series data from useSloData
 * Outputs:    SVG chart + date/percentage labels; renders empty message if no data
 * Constraints: No recharts dependency — uses hand-rolled SVG path math
 */
import type { ErrorBudgetPoint } from '../../hooks/useSloData'

export function ErrorBudgetChart({ points }: { points: ErrorBudgetPoint[] }) {
  if (points.length === 0)
    return <p className="text-xs text-brand-muted">No data yet</p>

  const W = 400
  const H = 80
  const maxBudget = 100
  const minBudget = 0

  const xs = points.map((_, i) => Math.round((i / (points.length - 1)) * W))
  const ys = points.map((p) =>
    Math.round(H - ((p.budgetPercent - minBudget) / (maxBudget - minBudget)) * H),
  )

  const pathD = xs
    .map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`)
    .join(' ')

  const latestPoint = points[points.length - 1]
  const latestBudget = latestPoint?.budgetPercent ?? 0
  const lineColor =
    latestBudget > 50 ? '#22c55e' : latestBudget > 20 ? '#eab308' : '#ef4444'

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        aria-label="Error budget over time"
        role="img"
      >
        {[0, 25, 50, 75, 100].map((pct) => {
          const y = Math.round(H - (pct / 100) * H)
          return (
            <g key={pct}>
              <line x1={0} y1={y} x2={W} y2={y} stroke="#374151" strokeWidth={1} />
              <text x={4} y={y - 2} fill="#6b7280" fontSize={8}>
                {pct}%
              </text>
            </g>
          )
        })}
        <path d={pathD} fill="none" stroke={lineColor} strokeWidth={2} />
        {xs.length > 0 && (
          <circle
            cx={xs[xs.length - 1]}
            cy={ys[ys.length - 1]}
            r={3}
            fill={lineColor}
          />
        )}
      </svg>
      <div className="mt-1 flex justify-between text-xs text-brand-muted">
        <span>{points[0] ? new Date(points[0].time).toLocaleDateString() : ''}</span>
        <span className="font-medium" style={{ color: lineColor }}>
          {latestBudget.toFixed(1)}% remaining
        </span>
        <span>
          {latestPoint ? new Date(latestPoint.time).toLocaleDateString() : ''}
        </span>
      </div>
    </div>
  )
}
